<template>
  <div class="checkbox__wrap" :class="checkboxStyleModificators">
    <label
      v-if="labelAsHtml"
      v-focusable="{ attachTabIndex: false }"
      :tabindex="labelTabIndex"
      class="checkbox__label checkbox__html text-body"
      key="html-label"
    >
      <input
        tabindex="-1"
        type="checkbox"
        class="checkbox__input"
        :checked="value"
        :disabled="disabled"
        @click="toggle"
      />
      <div class="checkbox__check">
        <a-icon
          v-if="value"
          :width="10"
          :height="8"
          class="checkbox__check-icon"
          :icon="$options.consts.ICON.CHECKBOX_CHECK"
        />
      </div>
      <span v-html="sanitizedLabel" class="checkbox__label-text" />
    </label>

    <label
      v-else
      v-focusable="{ attachTabIndex: false }"
      :tabindex="labelTabIndex"
      class="checkbox__label text-body"
      key="text-label"
    >
      <input
        tabindex="-1"
        type="checkbox"
        class="checkbox__input"
        :checked="value"
        :disabled="disabled"
        @click="toggle"
      />
      <div class="checkbox__check">
        <a-icon
          v-if="value"
          :width="10"
          :height="8"
          class="checkbox__check-icon"
          :icon="$options.consts.ICON.CHECKBOX_CHECK"
        />
      </div>
      <slot v-if="hasSlot" />
      <a-link
        v-else-if="labelHref"
        :href="labelHref"
        :rel="rel"
        :target="target"
      >
        <span class="checkbox__label-text">{{ label }}</span>
      </a-link>
      <span v-else class="checkbox__label-text">
        {{ label }}
      </span>
    </label>
    <a-validation-message :error="error" />
  </div>
</template>

<script>
import { prop } from 'ramda'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import AIcon, { ICON } from 'shared/AIcon'
import AValidationMessage from 'shared/AValidationMessage'
import { parseAndSanitizeHtml } from '@/utils/helpers/processHtml/parseAndSanitizeHtml'

export const CHECKBOX_STYLE = {
  BORDERED: 'BORDERED',
  REGULAR: 'REGULAR'
}

const CHECKBOX_STYLE_VALUES = Object.values(CHECKBOX_STYLE)

export const CHECKBOX_SIZE = {
  SMALL: 'SMALL',
  REGULAR: 'REGULAR'
}

const CHECKBOX_SIZE_VALUES = Object.values(CHECKBOX_SIZE)

export default {
  name: 'ACheckbox',
  components: { AValidationMessage, AIcon },
  props: {
    label: {
      type: String
    },
    labelHref: {
      type: String,
      default: ''
    },
    rel: {
      type: String
    },
    error: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    target: {
      type: Boolean
    },
    labelAsHtml: {
      type: Boolean,
      default: false
    },
    checkboxStyle: {
      validator: value => CHECKBOX_STYLE_VALUES.includes(value),
      default: CHECKBOX_STYLE.BORDERED
    },
    checkboxSize: {
      validator: value => CHECKBOX_SIZE_VALUES.includes(value),
      default: CHECKBOX_SIZE.REGULAR
    }
  },
  consts: {
    HTML_TAG,
    ICON
  },
  computed: {
    labelTabIndex() {
      return this.disabled || this.readOnly ? -1 : 0
    },
    checkboxStyleModificators() {
      return {
        'checkbox--style-regular':
          this.checkboxStyle === CHECKBOX_STYLE.REGULAR,
        'checkbox--size-small': this.checkboxSize === CHECKBOX_SIZE.SMALL,
        'checkbox--disabled': this.disabled,
        'checkbox--read-only': this.readOnly
      }
    },
    sanitizedLabel() {
      return parseAndSanitizeHtml(this.label)
    },
    hasSlot() {
      return prop('length', this.$slots.default)
    }
  },
  methods: {
    toggle() {
      this.$emit('input', !this.value)
    }
  }
}
</script>

<style lang="scss" scoped>
.checkbox__wrap {
  width: 100%;
  min-height: 20px;
  user-select: none;
  color: $c--black;
  position: relative;
  display: inline-block;
  cursor: pointer;

  @include mobile {
    font-size: 11px;
    line-height: 15px;
  }

  .checkbox__input {
    position: absolute;
    opacity: 0;
    cursor: inherit;
  }

  .checkbox__label {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    max-height: inherit;
    min-height: inherit;
    overflow: hidden;
    padding-left: 30px;
    cursor: inherit;

    &:focus {
      outline: none;
    }

    @include mobile {
      padding-left: 26px;
    }
  }

  .checkbox__label * {
    display: block;
  }

  .checkbox__label::before {
    height: 20px;
    width: 20px;
    background-color: $c--white;
    border: 2px solid $c--black;
    left: 0;
    top: 0;
  }

  &.checkbox--disabled {
    cursor: not-allowed;

    .checkbox__label {
      color: $c--gray;

      .checkbox__check {
        border-color: $c--gray;
      }
    }
  }

  &.checkbox--read-only {
    cursor: default;
  }

  .checkbox__label-text {
    /deep/ a {
      color: $c--mid-purple;
      text-decoration: underline;
    }
  }

  &.checkbox--style-regular .checkbox__label-text {
    /deep/ * {
      color: $c--white;
    }
  }

  &.checkbox--size-small {
    .checkbox__label {
      padding-left: 22px;

      &:focus {
        outline: none;
      }
    }
  }

  .checkbox__check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    position: absolute;
    top: 2px;
    left: 0;

    border-radius: 3px;
    border: 1px solid $c--dark-purple;
    background: $c--white;
  }

  .checkbox__check-icon {
    fill: $c--dark-purple;
    /deep/ .a-icon__icon {
      display: block;
    }
  }
}
</style>
